// src/components/GenerationsList.js

import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Divider,
  Checkbox,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  FormControlLabel,
  FormGroup,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Menu,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton"; // Correct import path
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarTodayIcon,
  Clear as ClearIcon,
  Star as StarIcon,
  StarBorder,
  FilterList as FilterListIcon,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useRouter } from "../util/router";
import { doc, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "../util/firebase";
import GenerationsCalendar from "./GenerationsCalendar";
import applicationMaterials from "./applicationMaterials";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import { getLogoUrl } from "../util/helpers";
import { Chip } from "@material-ui/core"; // Added Chip import

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default, // Use theme background
    minHeight: "100vh", // Ensure it takes full height
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  controlsContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap", // Allow wrapping on smaller screens
    gap: theme.spacing(2), // Consistent gap
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  searchField: {
    flexGrow: 1,
    minWidth: 250, // Slightly larger minWidth
  },
  sortSelect: {
    minWidth: 180, // Fixed width for sort dropdown
  },
  filterButton: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  filterButtonActive: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.main,
  },
  calendarIconButton: {
    // Removed background color, rely on default IconButton styling
    // boxShadow: theme.shadows[1], // Removed shadow for cleaner look
  },
  filtersContainer: {
    marginTop: theme.spacing(2), // Increased spacing
    padding: theme.spacing(2, 3), // More horizontal padding
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  filterGroupTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  statsContainer: {
    marginBottom: theme.spacing(3), // Increased spacing
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  statsTitle: {
    marginBottom: theme.spacing(1.5), // Spacing below title
  },
  statChip: {
    margin: theme.spacing(0.5), // Use margin for spacing around chips
  },
  statusIndicator: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  generationListContainer: {
    position: "relative",
    // Removed fixed maxHeight, let it grow naturally or control externally
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden", // Keep overflow hidden for scrollbar
    // Removed background color and shadow, list items will have their own
  },
  generationList: {
    paddingTop: 0, // Remove default padding
    paddingBottom: 0,
  },
  listItemPaper: {
    marginBottom: theme.spacing(2), // Space between items
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden", // Ensure collapse works well
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: theme.shadows[4], // Subtle lift on hover
    },
  },
  listItemContent: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2), // Consistent padding
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemCheckbox: {
    padding: theme.spacing(0, 1, 0, 0), // Adjust padding for checkbox
  },
  listItemAvatarContainer: {
    marginRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  companyAvatar: {
    width: theme.spacing(6), // Slightly larger avatar
    height: theme.spacing(6),
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.background.default, // Background for contrast
    border: `1px solid ${theme.palette.divider}`,
  },
  listItemTextContainer: {
    flexGrow: 1,
    minWidth: 0, // Prevent text overflow issues
  },
  jobTitle: {
    fontWeight: 600, // Bolder title
    fontSize: "1rem",
    display: "inline-block", // Keep status chip inline
    marginRight: theme.spacing(1),
  },
  statusBadge: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.75rem",
    fontWeight: 500,
    display: "inline-block", // Ensure it stays inline
    verticalAlign: "middle", // Align with text
  },
  companyName: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem", // Slightly smaller company name
    marginBottom: theme.spacing(0.5), // Space below company name
  },
  dateText: {
    fontSize: "0.8rem",
    color: theme.palette.text.hint, // Subtle color for date
  },
  listItemActions: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(1), // Space before actions
  },
  actionButton: {
    padding: theme.spacing(0.5), // Smaller padding for icon buttons
    color: theme.palette.text.secondary, // Default color
    transition: "color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.main, // Highlight on hover
      transform: "scale(1.1)",
    },
  },
  favoriteIcon: {
    color: theme.palette.warning.main, // Use theme color for favorite
    "&:hover": {
      color: theme.palette.warning.dark,
    },
  },
  expandIcon: {
    transition: "transform 0.3s ease",
  },
  expandIconOpen: {
    transform: "rotate(180deg)",
  },
  collapseContainer: {
    backgroundColor: theme.palette.background.default, // Slightly different background for collapse
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  nestedList: {
    padding: theme.spacing(1, 2, 2, 2), // Padding for nested items
  },
  nestedListItem: {
    padding: theme.spacing(1, 0), // Adjust padding
    borderRadius: theme.shape.borderRadius,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  materialIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5), // Space after icon
  },
  materialText: {
    fontWeight: 500,
  },
  progressContainer: {
    marginTop: theme.spacing(1.5), // More space above progress
    padding: theme.spacing(1, 0), // Padding around progress
  },
  progressStepper: {
    padding: 0,
    backgroundColor: "transparent",
    flexGrow: 1, // Allow stepper to take available space
  },
  progressStep: {
    padding: theme.spacing(0, 0.5), // Spacing between steps
  },
  progressStepButton: {
    padding: theme.spacing(0.5), // Compact button
    "& .MuiStepLabel-iconContainer": {
      paddingRight: theme.spacing(0.5), // Space before label
    },
    "& .MuiStepLabel-label": {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: theme.palette.text.secondary, // Default label color
      "&.MuiStepLabel-active": {
        color: theme.palette.primary.main, // Active color
        fontWeight: 600,
      },
      "&.MuiStepLabel-completed": {
        color: theme.palette.success.main, // Completed color
        fontWeight: 600,
      },
    },
    "& .MuiStepIcon-root": {
      fontSize: "1.5rem", // Larger icons
      color: theme.palette.action.disabled, // Default icon color
      "&.MuiStepIcon-active": {
        color: theme.palette.primary.main,
      },
      "&.MuiStepIcon-completed": {
        color: theme.palette.success.main,
      },
    },
  },
  progressStatusButton: {
    marginLeft: theme.spacing(1.5), // Space before status button
    fontSize: "0.75rem",
    textTransform: "none",
    minWidth: "auto",
    padding: theme.spacing(0.5, 1),
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3), // More padding
    backgroundColor: theme.palette.primary.dark, // Darker title background
    color: theme.palette.primary.contrastText,
  },
  dialogContent: {
    padding: theme.spacing(3), // More padding
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3), // Adjust padding
    backgroundColor: theme.palette.background.default, // Background for actions
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    marginTop: theme.spacing(4), // Add margin top
  },
  skeletonItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  deleteActionsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
}));

function parseTimestamp(timestamp) {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp.toDate === "function")
    return timestamp.toDate();
  if (typeof timestamp === "string") {
    let str = timestamp.replace(" at ", " ");
    str = str.replace(/\u202F/g, " ");
    str = str.replace(/UTC[\+\-]\d+/, "");
    return new Date(str);
  }
  return new Date();
}

// Helper function to format date
function formatDate(date) {
  if (!date) return "";
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(date).toLocaleDateString(undefined, options);
}

function parseSalary(salaryStr) {
  if (!salaryStr || typeof salaryStr !== "string") return null;
  try {
    salaryStr = salaryStr.replace(/[^\d\-–,]/g, "").trim();
    if (salaryStr.includes("-") || salaryStr.includes("–")) {
      const delimiter = salaryStr.includes("-") ? "-" : "–";
      const [minStr, maxStr] = salaryStr.split(delimiter);
      const min = parseInt(minStr.replace(/,/g, ""));
      const max = parseInt(maxStr.replace(/,/g, ""));
      if (!isNaN(min) && !isNaN(max)) return (min + max) / 2;
    }
    const salary = parseInt(salaryStr.replace(/,/g, ""));
    if (!isNaN(salary)) return salary;
    return null;
  } catch {
    return null;
  }
}

function hasMaterial(generation, key) {
  if (generation[key + "Json"]) return true;
  if (generation.content && generation.content[key + "Json"]) return true;
  if (
    generation.content &&
    Array.isArray(generation.content.items) &&
    generation.content.items.length > 0 &&
    generation.content.items[0][key + "Json"]
  ) {
    return true;
  }
  return false;
}

// Skeleton component for a single list item
const GenerationListItemSkeleton = ({ classes }) => (
  <>
    <ListItem className={classes.listItem}>
      <ListItemIcon className={classes.listItemAvatar}>
        <Skeleton variant="circular" width={40} height={40} />
      </ListItemIcon>
      <ListItemText
        primary={<Skeleton variant="text" width="60%" />}
        secondary={
          <>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton
              variant="rectangular"
              height={30}
              style={{ marginTop: 8 }}
            />
          </>
        }
      />
      <Skeleton
        variant="circular"
        width={24}
        height={24}
        style={{ marginLeft: 8 }}
      />
      <Skeleton
        variant="circular"
        width={24}
        height={24}
        style={{ marginLeft: 8 }}
      />
    </ListItem>
    <Divider />
  </>
);

function GenerationsList({
  auth,
  generations,
  setGenerations,
  loadingGenerations,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const router = useRouter();

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGenerations, setExpandedGenerations] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeletionMode, setIsDeletionMode] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sortOption, setSortOption] = useState("dateDesc");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterActive, setFilterActive] = useState(true);
  const [filterClosed, setFilterClosed] = useState(true);
  const [filterApplied, setFilterApplied] = useState(true);
  const [filterInterview, setFilterInterview] = useState(true);
  const [filterOffer, setFilterOffer] = useState(true);
  const [filterAccepted, setFilterAccepted] = useState(true);
  const [favorites, setFavorites] = useState([]);

  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleDeleteGeneration = useCallback(
    async (generationId) => {
      if (auth?.user) {
        const generationDocRef = doc(
          db,
          "users",
          auth.user.uid,
          "generations",
          generationId
        );
        try {
          await deleteDoc(generationDocRef);
          setSnackbar({
            open: true,
            message: "Generation deleted successfully.",
            severity: "success",
          });
          setGenerations((prevGenerations) =>
            prevGenerations.filter((gen) => gen.id !== generationId)
          );
        } catch (error) {
          console.error("Error deleting generation from Firestore:", error);
          setSnackbar({
            open: true,
            message: "Failed to delete generation.",
            severity: "error",
          });
        }
      }
    },
    [auth?.user, setGenerations]
  );

  const handleDeleteSelectedGenerations = useCallback(async () => {
    await Promise.all(
      selectedForDeletion.map((id) => handleDeleteGeneration(id))
    );
    setDeleteDialogOpen(false);
    setIsDeletionMode(false);
    setSelectedForDeletion([]);
  }, [selectedForDeletion, handleDeleteGeneration]);

  const handleGenerationClick = (generationId) => {
    setExpandedGenerations((prevExpanded) =>
      prevExpanded.includes(generationId)
        ? prevExpanded.filter((id) => id !== generationId)
        : [...prevExpanded, generationId]
    );
  };

  const handleToggleSelection = (id) => {
    setSelectedForDeletion((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id]
    );
  };

  const matchesStatusFilters = useCallback(
    (generation) => {
      const { progress = 0, status = "active" } = generation;
      if (status === "closed" && !filterClosed) return false;
      if (status !== "closed" && !filterActive) return false;
      if (progress === 0 && !filterApplied) return false;
      if (progress === 1 && !filterInterview) return false;
      if (progress === 2 && !filterOffer) return false;
      if (progress === 3 && !filterAccepted) return false;
      return true;
    },
    [
      filterActive,
      filterClosed,
      filterApplied,
      filterInterview,
      filterOffer,
      filterAccepted,
    ]
  );

  const filteredGenerations = useMemo(() => {
    const lowerQuery = debouncedSearch.toLowerCase();
    const result = generations.filter((generation) => {
      const searchMatch = debouncedSearch
        ? (generation.companyName &&
            generation.companyName.toLowerCase().includes(lowerQuery)) ||
          (generation.jobTitle &&
            generation.jobTitle.toLowerCase().includes(lowerQuery)) ||
          (generation.jobDetails?.jobDescription &&
            generation.jobDetails.jobDescription
              .toLowerCase()
              .includes(lowerQuery))
        : true;
      return searchMatch && matchesStatusFilters(generation);
    });
    const favSet = new Set(favorites);
    result.sort((a, b) => {
      const aFav = favSet.has(a.id) ? 1 : 0;
      const bFav = favSet.has(b.id) ? 1 : 0;
      if (aFav !== bFav) {
        return bFav - aFav; // favorites come first
      }
      if (sortOption === "salaryAsc" || sortOption === "salaryDesc") {
        const salaryA = parseSalary(a.jobDetails?.salary || "");
        const salaryB = parseSalary(b.jobDetails?.salary || "");
        if (salaryA === null && salaryB === null) return 0;
        if (salaryA === null) return 1;
        if (salaryB === null) return -1;
        return sortOption === "salaryAsc"
          ? salaryA - salaryB
          : salaryB - salaryA;
      } else if (sortOption === "companyAsc" || sortOption === "companyDesc") {
        const companyA = a.companyName?.toLowerCase() || "";
        const companyB = b.companyName?.toLowerCase() || "";
        if (companyA < companyB) return sortOption === "companyAsc" ? -1 : 1;
        if (companyA > companyB) return sortOption === "companyAsc" ? 1 : -1;
        return 0;
      } else {
        const dateA = parseTimestamp(a.timestamp);
        const dateB = parseTimestamp(b.timestamp);
        return sortOption === "dateAsc" ? dateA - dateB : dateB - dateA;
      }
    });
    return result;
  }, [
    generations,
    debouncedSearch,
    sortOption,
    favorites,
    matchesStatusFilters,
  ]);

  const stats = useMemo(() => {
    const total = filteredGenerations.length;
    const activeCount = filteredGenerations.filter(
      (ev) => ev.status !== "closed"
    ).length;
    const closedCount = filteredGenerations.filter(
      (ev) => ev.status === "closed"
    ).length;
    const appliedCount = filteredGenerations.filter(
      (ev) => ev.progress === 0
    ).length;
    const interviewsCount = filteredGenerations.filter(
      (ev) => ev.progress === 1
    ).length;
    const offersCount = filteredGenerations.filter(
      (ev) => ev.progress === 2
    ).length;
    const acceptedCount = filteredGenerations.filter(
      (ev) => ev.progress === 3
    ).length;
    return {
      total,
      activeCount,
      closedCount,
      appliedCount,
      interviewsCount,
      offersCount,
      acceptedCount,
    };
  }, [filteredGenerations]);

  const clearAllFilters = () => {
    setSearchQuery("");
    setSortOption("dateDesc");
    setFilterActive(true);
    setFilterClosed(true);
    setFilterApplied(true);
    setFilterInterview(true);
    setFilterOffer(true);
    setFilterAccepted(true);
  };

  const handleEditGeneration = useCallback(
    async (generationId, updatedData) => {
      if (!auth?.user) return;
      const generationDocRef = doc(
        db,
        "users",
        auth.user.uid,
        "generations",
        generationId
      );

      // Prepare data for Firestore update
      let dataToUpdate = { ...updatedData }; // Copy original data

      // Check if updatedData contains 'start' (indicating a calendar drag/drop)
      if (updatedData.start) {
        // Convert the start date from the calendar event to a format Firestore understands
        // (e.g., ISO string or Firestore Timestamp)
        // Assuming 'start' is a Date object from react-big-calendar
        dataToUpdate = {
          ...dataToUpdate, // Keep any other potential updates
          timestamp: updatedData.start.toISOString(), // Update the timestamp field
        };
        // Remove start/end as they are not direct Firestore fields for the generation
        delete dataToUpdate.start;
        delete dataToUpdate.end;
      }

      try {
        // Use the prepared dataToUpdate object
        await updateDoc(generationDocRef, dataToUpdate);

        // Update local state optimistically
        setGenerations((prevGenerations) =>
          prevGenerations.map((gen) =>
            // Apply the same prepared updates to the local state
            gen.id === generationId ? { ...gen, ...dataToUpdate } : gen
          )
        );
        setSnackbar({
          open: true,
          // Adjust message based on whether it was a reschedule or other edit
          message: updatedData.start
            ? "Generation date updated successfully!"
            : "Generation updated successfully!",
          severity: "success",
        });
      } catch (error) {
        console.error("Error updating generation in Firestore:", error);
        setSnackbar({
          open: true,
          message: "Failed to reschedule generation.",
          severity: "error",
        });
      }
    },
    [auth?.user, setGenerations]
  );

  const handleUpdateProgress = useCallback(
    async (generationId, progress, status) => {
      if (!auth?.user) {
        console.warn("No authenticated user found, cannot update progress.");
        return;
      }
      const generationDocRef = doc(
        db,
        "users",
        auth.user.uid,
        "generations",
        generationId
      );
      try {
        await updateDoc(generationDocRef, { progress, status });
        setGenerations((prevGenerations) =>
          prevGenerations.map((gen) =>
            gen.id === generationId ? { ...gen, progress, status } : gen
          )
        );
        setSnackbar({
          open: true,
          message: "Progress updated successfully!",
          severity: "success",
        });
      } catch (error) {
        console.error("Error updating progress in Firestore:", error);
        setSnackbar({
          open: true,
          message: "Failed to update progress.",
          severity: "error",
        });
      }
    },
    [auth?.user, setGenerations]
  );

  const handleGenerationSelect = useCallback(
    (generationId, materialKey) => {
      localStorage.setItem("selectedGenerationId", generationId);
      if (materialKey) {
        localStorage.setItem("selectedApplicationMaterial", materialKey);
      } else {
        localStorage.removeItem("selectedApplicationMaterial");
      }
      router.push("/pro");
    },
    [router]
  );

  // Helper to get status color
  const getStatusColor = (status, progress) => {
    if (status === "closed") return theme.palette.error.main;
    switch (progress) {
      case 0:
        return theme.palette.primary.main;
      case 1:
        return theme.palette.warning.main;
      case 2:
        return theme.palette.success.light;
      case 3:
        return theme.palette.success.dark;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h4" component="h1" className={classes.pageHeader}>
        Application Dashboard
      </Typography>

      {/* Stats Section */}
      <Paper className={classes.statsContainer} elevation={1}>
        <Typography variant="h6" className={classes.statsTitle}>
          Status Overview
        </Typography>
        <Box display="flex" flexWrap="wrap">
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.grey[500] }}
              />
            }
            label={`Total: ${stats.total}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.info.main }}
              />
            }
            label={`Active: ${stats.activeCount}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.error.main }}
              />
            }
            label={`Not Selected: ${stats.closedCount}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.primary.main }}
              />
            }
            label={`Applied: ${stats.appliedCount}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.warning.main }}
              />
            }
            label={`Interview: ${stats.interviewsCount}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.success.light }}
              />
            }
            label={`Offer: ${stats.offersCount}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={
              <div
                className={classes.statusIndicator}
                style={{ backgroundColor: theme.palette.success.dark }}
              />
            }
            label={`Accepted: ${stats.acceptedCount}`}
            className={classes.statChip}
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Controls Section */}
      <Paper className={classes.controlsContainer} elevation={1}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          placeholder="Search Applications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Tooltip title="Clear Search">
                  <IconButton
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    size="small"
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            "aria-label": "Search Generations",
          }}
        />
        <FormControl
          variant="outlined"
          size="small"
          className={classes.sortSelect}
        >
          <InputLabel id="sort-by-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-label"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            label="Sort By"
            aria-label="Sort Generations"
          >
            <MenuItem value="dateDesc">Date (Newest)</MenuItem>
            <MenuItem value="dateAsc">Date (Oldest)</MenuItem>
            <MenuItem value="companyAsc">Company (A-Z)</MenuItem>
            <MenuItem value="companyDesc">Company (Z-A)</MenuItem>
            <MenuItem value="salaryDesc">Salary (High to Low)</MenuItem>
            <MenuItem value="salaryAsc">Salary (Low to High)</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title={filtersOpen ? "Hide Filters" : "Show Filters"}>
          <Box
            className={`${classes.filterButton} ${
              filtersOpen ? classes.filterButtonActive : ""
            }`}
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-label="Toggle filters"
            aria-expanded={filtersOpen}
          >
            <FilterListIcon
              fontSize="small"
              style={{ marginRight: theme.spacing(0.5) }}
            />
            <Typography variant="body2">Filters</Typography>
            {filtersOpen ? (
              <ExpandLess
                fontSize="small"
                style={{ marginLeft: theme.spacing(0.5) }}
              />
            ) : (
              <ExpandMore
                fontSize="small"
                style={{ marginLeft: theme.spacing(0.5) }}
              />
            )}
          </Box>
        </Tooltip>
        <Tooltip title="Open Calendar View">
          <IconButton
            className={classes.calendarIconButton}
            onClick={() => setCalendarOpen(true)}
            aria-label="View Calendar"
            size="small"
          >
            <CalendarTodayIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Filters Collapse Section */}
      <Collapse in={filtersOpen} timeout="auto">
        <Paper
          className={classes.filtersContainer}
          elevation={0}
          variant="outlined"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Filter Options</Typography>
            <Button
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              size="small"
              color="primary"
            >
              Clear All
            </Button>
          </Box>
          <Box mb={2}>
            <Typography
              className={classes.filterGroupTitle}
              variant="subtitle2"
            >
              Application Status
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterActive}
                    onChange={(e) => setFilterActive(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterClosed}
                    onChange={(e) => setFilterClosed(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Not Selected"
              />
            </FormGroup>
          </Box>
          <Box>
            <Typography
              className={classes.filterGroupTitle}
              variant="subtitle2"
            >
              Application Progress
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterApplied}
                    onChange={(e) => setFilterApplied(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Applied"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterInterview}
                    onChange={(e) => setFilterInterview(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Interview"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterOffer}
                    onChange={(e) => setFilterOffer(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Offer"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterAccepted}
                    onChange={(e) => setFilterAccepted(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Accepted"
              />
            </FormGroup>
          </Box>
        </Paper>
      </Collapse>

      {/* Loading State */}
      {loadingGenerations && (
        <Box className={classes.generationListContainer}>
          <List disablePadding>
            {[...Array(5)].map((_, index) => (
              <GenerationListItemSkeleton key={index} classes={classes} />
            ))}
          </List>
        </Box>
      )}

      {/* Data Loaded State */}
      {!loadingGenerations && generations.length > 0 ? (
        <>
          <Box className={classes.generationListContainer}>
            {/* Use PerfectScrollbar only if needed, or manage scroll on the Box */}
            <List className={classes.generationList} disablePadding>
              {filteredGenerations.map((generation) => {
                const isExpanded = expandedGenerations.includes(generation.id);
                const isFavorite = favorites.includes(generation.id);
                const materialsAvailable = Object.keys(
                  applicationMaterials
                ).filter((key) => hasMaterial(generation, key));
                const formattedDate = formatDate(
                  parseTimestamp(generation.timestamp)
                );
                const statusColor = getStatusColor(
                  generation.status,
                  generation.progress
                );

                return (
                  <Paper
                    key={generation.id}
                    className={classes.listItemPaper}
                    variant="outlined"
                  >
                    <Box
                      className={classes.listItemContent}
                      onClick={() => {
                        if (isDeletionMode) {
                          handleToggleSelection(generation.id);
                        } else {
                          handleGenerationClick(generation.id);
                        }
                      }}
                      aria-expanded={isExpanded}
                      aria-label={`Generation for ${generation.companyName} as ${generation.jobTitle}`}
                    >
                      {isDeletionMode && (
                        <Checkbox
                          className={classes.listItemCheckbox}
                          edge="start"
                          checked={selectedForDeletion.includes(generation.id)}
                          tabIndex={-1}
                          disableRipple
                          onChange={(e) => {
                            e.stopPropagation(); // Prevent click propagating to the Box
                            handleToggleSelection(generation.id);
                          }}
                          color="primary"
                          size="small"
                          inputProps={{
                            "aria-label": `Select generation for ${generation.companyName}`,
                          }}
                        />
                      )}
                      <Box className={classes.listItemAvatarContainer}>
                        <Avatar
                          className={classes.companyAvatar}
                          src={getLogoUrl(generation.companyName)}
                          alt={`${generation.companyName} Logo`}
                          variant="rounded" // Use rounded variant for logos
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "";
                          }}
                        />
                      </Box>
                      <Box className={classes.listItemTextContainer}>
                        <Typography
                          variant="h6"
                          className={classes.jobTitle}
                          noWrap
                        >
                          {generation.jobTitle || "Unknown Title"}
                        </Typography>
                        {generation.status === "closed" && (
                          <Chip
                            label="Not Selected"
                            size="small"
                            className={classes.statusBadge}
                            style={{
                              backgroundColor: theme.palette.error.main,
                              color: theme.palette.error.contrastText,
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          className={classes.companyName}
                          noWrap
                        >
                          {generation.companyName || "Unknown Company"}
                        </Typography>
                        <Typography className={classes.dateText}>
                          {formattedDate}
                        </Typography>
                        {/* Progress Stepper */}
                        <Box
                          className={classes.progressContainer}
                          display="flex"
                          alignItems="center"
                        >
                          <Stepper
                            className={classes.progressStepper}
                            activeStep={generation.progress ?? -1} // Use -1 if undefined
                            alternativeLabel
                            nonLinear
                            connector={<></>} // Hide default connectors
                          >
                            {["Applied", "Interview", "Offer", "Accepted"].map(
                              (label, index) => (
                                <Step
                                  key={label}
                                  className={classes.progressStep}
                                >
                                  <Tooltip title={label}>
                                    <StepButton
                                      icon={<></>} // Hide default icon, we use StepLabel styling
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (generation.status !== "closed") {
                                          handleUpdateProgress(
                                            generation.id,
                                            index,
                                            "active"
                                          );
                                        }
                                      }}
                                      completed={
                                        generation.status !== "closed" &&
                                        (generation.progress ?? -1) >= index
                                      }
                                      disabled={generation.status === "closed"}
                                      className={classes.progressStepButton}
                                    >
                                      {/* Label is styled via CSS */}
                                    </StepButton>
                                  </Tooltip>
                                </Step>
                              )
                            )}
                          </Stepper>
                          {/* Status Change Button */}
                          {generation.status === "closed" ? (
                            <Tooltip title="Reactivate Application">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                className={classes.progressStatusButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateProgress(
                                    generation.id,
                                    generation.progress || 0,
                                    "active"
                                  );
                                }}
                              >
                                Reactivate
                              </Button>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Mark as Not Selected">
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                className={classes.progressStatusButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateProgress(
                                    generation.id,
                                    generation.progress || 0,
                                    "closed"
                                  );
                                }}
                              >
                                Close
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      {/* Action Icons */}
                      {!isDeletionMode && (
                        <Box className={classes.listItemActions}>
                          <Tooltip
                            title={
                              isFavorite
                                ? "Remove from Favorites"
                                : "Add to Favorites"
                            }
                          >
                            <IconButton
                              className={classes.actionButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(generation.id);
                              }}
                              aria-label="Toggle Favorite"
                              size="small"
                            >
                              {isFavorite ? (
                                <StarIcon className={classes.favoriteIcon} />
                              ) : (
                                <StarBorder />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              isExpanded ? "Collapse Details" : "Expand Details"
                            }
                          >
                            <IconButton
                              className={`${classes.actionButton} ${
                                classes.expandIcon
                              } ${isExpanded ? classes.expandIconOpen : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerationClick(generation.id);
                              }}
                              aria-label={
                                isExpanded
                                  ? "Collapse details"
                                  : "Expand details"
                              }
                              size="small"
                            >
                              <ExpandMore />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    {/* Collapsible Section */}
                    <Collapse
                      in={isExpanded && !isDeletionMode}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box className={classes.collapseContainer}>
                        <List
                          className={classes.nestedList}
                          component="div"
                          disablePadding
                        >
                          {materialsAvailable.length > 0 ? (
                            materialsAvailable.map((key) => {
                              const MaterialIcon =
                                applicationMaterials[key].icon;
                              return (
                                <ListItem
                                  button
                                  key={key}
                                  className={classes.nestedListItem}
                                  onClick={() =>
                                    handleGenerationSelect(generation.id, key)
                                  }
                                  aria-label={`Select ${applicationMaterials[key].title}`}
                                >
                                  <MaterialIcon
                                    className={classes.materialIcon}
                                    aria-hidden="true"
                                  />
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        className={classes.materialText}
                                      >
                                        {applicationMaterials[key].title}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              );
                            })
                          ) : (
                            <ListItem className={classes.nestedListItem}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    No application materials available.
                                  </Typography>
                                }
                              />
                            </ListItem>
                          )}
                        </List>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </List>
          </Box>
          {filteredGenerations.length === 0 && !loadingGenerations && (
            <Box className={classes.emptyState}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Matches Found
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Try adjusting your search or filters.
              </Typography>
              <Button
                color="primary"
                onClick={clearAllFilters}
                style={{ marginTop: theme.spacing(2) }}
                variant="outlined"
              >
                Clear All Filters
              </Button>
            </Box>
          )}
        </>
      ) : (
        !loadingGenerations && (
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              No Applications Yet
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Start generating application materials to see them here.
            </Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={() => router.push("/")}
              size="large"
            >
              Create New Application
            </Button>
          </Box>
        )
      )}

      {/* Delete Actions */}
      {generations.length > 0 && (
        <Paper className={classes.deleteActionsContainer} elevation={2}>
          {isDeletionMode ? (
            <>
              <Button
                onClick={() => {
                  setIsDeletionMode(false);
                  setSelectedForDeletion([]);
                }}
                aria-label="Cancel deletion mode"
                variant="text" // Use text variant for cancel
                style={{ marginRight: theme.spacing(1) }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedForDeletion.length > 0) setDeleteDialogOpen(true);
                }}
                color="secondary"
                variant="contained"
                aria-label="Delete selected generations"
                disabled={selectedForDeletion.length === 0}
                startIcon={<DeleteIcon />}
              >
                Delete ({selectedForDeletion.length})
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setIsDeletionMode(true);
                setSelectedForDeletion([]);
              }}
              color="secondary"
              variant="outlined"
              startIcon={<DeleteIcon />}
              aria-label="Enter deletion mode"
            >
              Manage Applications
            </Button>
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-confirmation-dialog-title"
        PaperProps={{ style: { borderRadius: theme.shape.borderRadius } }}
      >
        <DialogTitle
          id="delete-confirmation-dialog-title"
          className={classes.dialogTitle}
          disableTypography
        >
          <Typography variant="h6">Confirm Deletion</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setDeleteDialogOpen(false)}
            style={{ color: theme.palette.primary.contrastText }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to permanently delete the selected{" "}
            {selectedForDeletion.length} application(s)?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This action cannot be undone.
          </Typography>
          {/* Optional: Show list of items to be deleted */}
          {/* <Paper variant="outlined" style={{ maxHeight: 150, overflow: 'auto', marginTop: theme.spacing(2) }}>
            <List dense>
              {selectedForDeletion.map(id => {
                const gen = generations.find(g => g.id === id);
                return gen ? <ListItem key={id}><ListItemText primary={gen.jobTitle} secondary={gen.companyName} /></ListItem> : null;
              })}
            </List>
          </Paper> */}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSelectedGenerations}
            color="secondary"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        fullWidth
        maxWidth="lg" // Use larger width for calendar
        aria-labelledby="generations-calendar-dialog-title"
        PaperProps={{
          style: { borderRadius: theme.shape.borderRadius, height: "90vh" },
        }} // Adjust height
      >
        <DialogTitle
          id="generations-calendar-dialog-title"
          className={classes.dialogTitle}
          disableTypography
        >
          <Typography variant="h6">Application Calendar</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setCalendarOpen(false)}
            style={{ color: theme.palette.primary.contrastText }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: 0, overflow: "hidden" }}>
          {" "}
          {/* Remove padding, manage inside component */}
          {loadingGenerations ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress aria-label="Loading calendar" />
            </Box>
          ) : (
            <GenerationsCalendar
              generations={generations}
              onSelectGeneration={handleGenerationSelect}
              onEditGeneration={handleEditGeneration}
              onDeleteGeneration={handleDeleteGeneration}
              onUpdateProgress={handleUpdateProgress}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} // Position bottom-right
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GenerationsList;
